import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getInstagramReplyModeLabelKey,
  type InstagramComposerContext,
  type InstagramReplyMode,
} from '../utils/instagramComposerUtils';
import { truncateMessageSnippet } from '../utils/messageUtils';
import styles from './ConversationDetails.module.css';

export interface InstagramComposerReplyTargetProps {
  context: InstagramComposerContext;
  replyMode: InstagramReplyMode;
  selectedCommentMessageId?: string | null;
  onReplyModeChange: (mode: InstagramReplyMode) => void;
  onCommentTargetChange: (messageId: string) => void;
  onClear: () => void;
  onJumpToTarget: () => void;
}

export const InstagramComposerReplyTarget: React.FC<InstagramComposerReplyTargetProps> = ({
  context,
  replyMode,
  selectedCommentMessageId,
  onReplyModeChange,
  onCommentTargetChange,
  onClear,
  onJumpToTarget,
}) => {
  const { t } = useTranslation('conversations');
  const activeTargetId = selectedCommentMessageId ?? null;
  const selectedTarget = context.unrepliedCommentOptions.find(option => option.messageId === activeTargetId);
  const showTargetSelect = context.unrepliedCommentOptions.length > 1;
  const commentModes = context.availableReplyModes.filter(
    mode => mode === 'private_comment' || mode === 'public_comment',
  );
  const showModeToggle = commentModes.length > 1;
  const shellClass = replyMode === 'public_comment'
    ? styles.composerInstagramReplyTargetPublic
    : styles.composerInstagramReplyTargetPrivate;

  if (!selectedTarget) {
    return null;
  }

  const formatOptionLabel = (option: typeof selectedTarget): string => {
    const postLabel = t(option.postLabelKey);
    const snippet = truncateMessageSnippet(
      option.commentText || option.postCaption || t('instagramContentPreviewNoCaption'),
      72,
    );
    return `${postLabel} · ${snippet}`;
  };

  return (
    <div className={`${styles.composerInstagramReplyTarget} ${shellClass}`}>
      <div className={styles.composerInstagramReplyTargetRow}>
        <button
          type="button"
          className={styles.composerInstagramReplyTargetJump}
          onClick={onJumpToTarget}
        >
          <div className={styles.composerInstagramReplyTargetBody}>
            <span className={styles.composerInstagramReplyTargetLabel}>
              {t('instagramComposerReplyTargetLabel', { mode: t(getInstagramReplyModeLabelKey(replyMode)) })}
            </span>
            <span className={styles.composerInstagramReplyTargetMeta}>
              {t(selectedTarget.postLabelKey)}
            </span>
            <span className={styles.composerInstagramReplyTargetText}>
              {truncateMessageSnippet(
                selectedTarget.commentText || selectedTarget.postCaption || t('instagramContentPreviewNoCaption'),
                140,
              )}
            </span>
          </div>
        </button>
        <button
          type="button"
          className={styles.composerQuotedMessageClear}
          onClick={onClear}
          title={t('instagramComposerReplyTargetClear')}
        >
          {t('close', { ns: 'common' })}
        </button>
      </div>

      {showTargetSelect ? (
        <label className={styles.composerInstagramReplyTargetSelectWrap}>
          <span className={styles.composerInstagramReplyTargetSelectLabel}>{t('instagramReplyTargetSelectLabel')}</span>
          <select
            className={styles.composerInstagramReplyTargetSelect}
            value={selectedTarget.messageId}
            onChange={event => onCommentTargetChange(event.target.value)}
            aria-label={t('instagramReplyTargetSelectLabel')}
          >
            {context.unrepliedCommentOptions.map(option => (
              <option key={option.messageId} value={option.messageId}>
                {formatOptionLabel(option)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {showModeToggle ? (
        <div className={styles.composerInstagramReplyModeToggle} role="group" aria-label={t('instagramReplyModeLabel')}>
          {commentModes.map(mode => (
            <button
              key={mode}
              type="button"
              className={`${styles.composerInstagramReplyModeButton} ${replyMode === mode ? styles.composerInstagramReplyModeButtonActive : ''}`}
              aria-pressed={replyMode === mode}
              onClick={() => onReplyModeChange(mode)}
            >
              {t(getInstagramReplyModeLabelKey(mode))}
            </button>
          ))}
        </div>
      ) : context.pendingCommentSurface === 'live_comment' ? (
        <span className={styles.composerInstagramReplyTargetHint}>{t('instagramLiveCommentBadge')}</span>
      ) : null}
    </div>
  );
};
